/**
 * Shared kontakty list-filter state. The list page (`KontaktyList`) and the
 * detail's master-list rail (`KontaktNarrowRail` via `KontaktDetail`) both read
 * the same persisted filters so the rail mirrors the list the user came from.
 *
 * Brand/seller/showMine/signedOnly live in localStorage (a seller/brand choice
 * should survive a round-trip to a contact detail); `type` and the name search
 * ride the URL (mirroring vozidla) so the detail rail and bookmarks see them.
 */

import { searchParamString } from '@/lib/list-search';
import type { ContactListFilters, ContactType } from './api';

/**
 * Search-param shape shared between /kontakty and /kontakty/$id.
 * Persisting these in the URL means the narrow rail on the detail page
 * matches the filter the user had on the list page (and bookmarks /
 * back-forward navigation work as expected).
 */
export interface KontaktySearch {
  type?: ContactType;
  search?: string;
}

const VALID_TYPES: ReadonlySet<ContactType> = new Set([
  'rozjednane',
  'ukoncene',
  'vsechny',
]);

/** TanStack Router `validateSearch` — coerces unknown query strings into
 *  the typed shape, dropping anything we don't recognize. */
export function parseKontaktySearch(input: Record<string, unknown>): KontaktySearch {
  const out: KontaktySearch = {};
  if (typeof input.type === 'string' && VALID_TYPES.has(input.type as ContactType)) {
    out.type = input.type as ContactType;
  }
  const search = searchParamString(input.search);
  if (search) out.search = search;
  return out;
}

export const FILTERS_STORAGE_KEY = 'kontakty-list-filters';

export type StoredFilters = {
  brandId: number | null;
  sellerId: number | null;
  showMine: boolean;
  signedOnly: boolean;
};

export const DEFAULT_FILTERS: StoredFilters = {
  brandId: null,
  sellerId: null,
  showMine: true,
  signedOnly: false,
};

/** Read the persisted filters; a corrupt/unavailable read falls back to defaults. */
export function readStoredFilters(): StoredFilters {
  if (typeof window === 'undefined') return DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<StoredFilters>;
    return {
      brandId: typeof parsed.brandId === 'number' ? parsed.brandId : null,
      sellerId: typeof parsed.sellerId === 'number' ? parsed.sellerId : null,
      showMine: typeof parsed.showMine === 'boolean' ? parsed.showMine : true,
      signedOnly: typeof parsed.signedOnly === 'boolean' ? parsed.signedOnly : false,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

/**
 * Compose the `ContactListFilters` the API expects from the resolved `type`,
 * the persisted filters, and the current user id (for the "Moje" toggle). Mirrors
 * the `filters` memo in `KontaktyList` so both surfaces query identically.
 */
export function buildContactFilters(opts: {
  type: ContactType;
  stored: StoredFilters;
  myId: number | null;
  name?: string;
  limit?: string;
}): ContactListFilters {
  const { type, stored, myId, name, limit } = opts;
  const effectiveSellerId =
    stored.sellerId ?? (stored.showMine && myId ? myId : null);
  return {
    type,
    ...(name ? { name } : {}),
    ...(stored.brandId ? { brand: String(stored.brandId) } : {}),
    ...(effectiveSellerId ? { seller: String(effectiveSellerId) } : {}),
    ...(stored.signedOnly && type !== 'rozjednane' ? { podpis_objednavky: 'Ano' } : {}),
    ...(limit ? { limit } : {}),
  };
}
